import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { getDecisionCriteria, notifyApps, rankApps } from '../../api/decisions.api';
import { useMediaQuery } from 'react-responsive';
import AppSkin1 from './skins/AppSkin1';
import AppSkin2 from './skins/AppSkin2';
import AppSkin3 from './skins/AppSkin3';
import AppSkin4 from './skins/AppSkin4';
import AppSkin5 from './skins/AppSkin5';
import AppSkin6 from './skins/AppSkin6';
import AppSkin7 from './skins/AppSkin7';
import AppSkin8 from './skins/AppSkin8';
import AppSkin9 from './skins/AppSkin9';
import MobileSkin1 from './skins/mobile/MobileSkin1';
import { applyAppTheme, transformCriteriaData } from '../../utils';
import AppCriteriaFields from './app-components/AppCriteriaFields';
import AppCriteriaSettings from './app-components/CriteriaSettingsV2';
import AppProductResults from './app-components/AppProductResults';
import HorizontalLinearStepper from '../horizontal-linear-stepper/HorizontalLinearStepper';
import Loading from '../loading/Loading';
import { useAppConfig } from '../../context/app-configs-context';
import { allowedSkins } from '../../shared/consts';
import { AppCriteria, AppResultInfo, AppSkinProps, CriteriaConstraint, PageInfo } from '../../types/decisions';
import { useAuth } from '../../context/auth-context';
import { useEvents } from '../../hooks/useEvents';
import { useAppTypeGuard } from '../../hooks/useAppTypeGuard';
import OraChatBot from '../appContents/OraChatBot/OraChatBot';
import { useError } from '../../context/error-context';
import './application.scss';

const appSkins = {
    skin1: AppSkin1,
    skin2: AppSkin2,
    skin3: AppSkin3,
    skin4: AppSkin4,
    skin5: AppSkin5,
    skin6: AppSkin6,
    skin7: AppSkin7,
    skin8: AppSkin8,
    skin9: AppSkin9,
};

interface ApplicationV2Props {
    criteriaData: AppCriteria;
    setCriteriaData: React.Dispatch<React.SetStateAction<AppCriteria>>;
    criteriaConstraints: {
        [key: string]: CriteriaConstraint[]
    };
    initialCriteriaData: React.MutableRefObject<AppCriteria>;
    pageInfo: PageInfo;
    setPageInfo: React.Dispatch<React.SetStateAction<PageInfo>>;
    setResults: React.Dispatch<React.SetStateAction<AppResultInfo[]>>;
    setResultLoading: React.Dispatch<React.SetStateAction<boolean>>;
    results: AppResultInfo[];
    setCriteriaConstraints: React.Dispatch<React.SetStateAction<{
        [key: string]: CriteriaConstraint[]
    }>>;
    onRerank: AppSkinProps['onRerank'];
    onRerankMany: AppSkinProps['onRerankMany'];
    onPageChange: AppSkinProps['onPageChange'];
    onSliderChange: AppSkinProps['onSliderChange'];
    onSliderBulkChange: AppSkinProps['onSliderBulkChange'];
    onTradeoffValueChange: AppSkinProps['onTradeoffValueChange'];
    onCriteriaApply: AppSkinProps['onCriteriaApply'];
    onResetTradeOffs: AppSkinProps['onResetTradeOffs'];
    resultLoading: AppSkinProps['resultLoading'];
    selectedProductIds: AppSkinProps['selectedProductIds'];
    setSelectedProductIds: AppSkinProps['setSelectedProductIds'];
    showSavedSettings?: boolean;
    shouldRunSkin9?: boolean;
}

const ApplicationV2 = ({
    criteriaData,
    criteriaConstraints,
    setCriteriaData,
    initialCriteriaData,
    pageInfo,
    setPageInfo,
    setResults,
    setResultLoading,
    results,
    setCriteriaConstraints,
    onRerank,
    onRerankMany,
    onPageChange,
    onSliderChange,
    onSliderBulkChange,
    onTradeoffValueChange,
    onCriteriaApply,
    onResetTradeOffs,
    resultLoading,
    selectedProductIds,
    setSelectedProductIds,
    showSavedSettings = false,
    shouldRunSkin9 = false,
}: ApplicationV2Props) => {
    const { appConfigs, setAppConfigs } = useAppConfig();
    const { emit } = useEvents();
    const { id } = useParams<{ id: string }>();
    const { isAuthLoading } = useAuth();
    const { showError } = useError();

    useAppTypeGuard(criteriaData, 'decision');
    const [ searchParams ] = useSearchParams();
    const dataset = searchParams.get('dataset') || 'main';
    const patientId = searchParams.get('patientId') || '';
    const activityRowId = searchParams.get('tradeoff_id') || searchParams.get('activity_row_id') || '';
    const hasActivityRowSettings = Boolean(activityRowId);
    const [ currentStep, setCurrentStep ] = useState(0);
    const [ criteriaLoading, setCriteriaLoading ] = useState<boolean>(true);
    const [ criteriaLoadKey, setCriteriaLoadKey ] = useState(0);

    const handleApiError = useCallback((err: any, description: string) => {
        const statusCode = Number(err?.response?.status ?? err?.status ?? 500);

        if (statusCode === 401) {
            emit('http-401', {
                code: statusCode,
            });

            return;
        }

        showError({
            code: statusCode,
            message: 'API Error',
            description,
            showRefresh: true,
        });
    }, [
        emit,
        showError,
    ]);

    const onNotifyApply = useCallback((notifyDataset: string = dataset) => {
        if (!id) {
            return;
        }

        notifyApps(
            transformCriteriaData(criteriaData, criteriaConstraints),
            notifyDataset,
        )
        .catch((err) => {
            handleApiError(err, 'Failed to notify');
        });
    }, [
        id,
        criteriaData,
        criteriaConstraints,
        dataset,
        handleApiError,
    ]);

    const isSkinAllowed = allowedSkins.includes(criteriaData?.config.skin || 'skin3');

    const isMobileDoctorSkin = useMediaQuery({ query: '(max-width: 1080px)' });
    const isMobile = useMediaQuery({ query: '(max-width: 768px)' });

    useEffect(() => {
        if (isAuthLoading) {
            return;
        }

        if (!id) {
            setCriteriaLoading(false);
            return;
        }

        setCurrentStep(0);
        setResults([]);
        setCriteriaLoading(true);
        setResultLoading(true);

        getDecisionCriteria(
            id,
            dataset,
            hasActivityRowSettings
                ? activityRowId
                : undefined,
        )
        .then(response => {
            initialCriteriaData.current = response;

            setCriteriaData(response);
            setAppConfigs(response.view_config);

            applyAppTheme(
                response.view_config.colors,
            );

            setCriteriaLoadKey(prev => prev + 1);
        })
        .catch((err) => {
            handleApiError(
                err,
                'Failed to fetch data',
            );
        })
        .finally(() => {
            setCriteriaLoading(false);
        });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        id,
        dataset,
        activityRowId,
        isAuthLoading,
    ]);

    useEffect(() => {
        if (criteriaLoading) {
            return;
        }

        if (!criteriaData.id || !isSkinAllowed) {
            return;
        }

        setResultLoading(true);

        rankApps(
            transformCriteriaData(
                criteriaData,
                criteriaConstraints,
            ),
            pageInfo.page_size,
            pageInfo.current_page,
            dataset,
            showSavedSettings && !hasActivityRowSettings,
            patientId,
        )
        .then(response => {
            setResults(response.results);

            setPageInfo({
                current_page: response.current_page,
                start: response.start,
                end: response.end,
                total: response.total,
                total_pages: response.total_pages,
                page_size: pageInfo.page_size,
            });

            if (
                showSavedSettings &&
                !hasActivityRowSettings
            ) {
                setCriteriaData(prevCriteriaData => {
                    return {
                        ...prevCriteriaData,
                        fields: prevCriteriaData.fields.map(field => {
                            const newFieldInfo =
                                response.criteria[field.id] || {
                                    importance: field.importance,
                                    value: field.val,
                                };

                            return {
                                ...field,
                                ...newFieldInfo,
                            };
                        }),
                    };
                });
            }
        })
        .catch((err) => {
            handleApiError(
                err,
                'Failed to fetch data',
            );
        })
        .finally(() => {
            setResultLoading(false);
        });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        criteriaData.id,
        criteriaLoading,
        criteriaLoadKey,
    ]);

    if (
        !id ||
        criteriaLoading ||
        criteriaData.config.skin === 'skin0'
    ) {
        return <Loading/>;
    }

    if (!criteriaLoading && !isSkinAllowed) {
        return (
            <div className="application">
                <div className="warning-banner">
                    This application is under construction. Visit{' '}
                    <Link
                        to={`https://legacy2.sparkdit.com/decisions/view/${id}`}
                        target="_blank"
                    >
                        the old version
                    </Link>{' '}
                    of the application.
                </div>
            </div>
        );
    }

    const journey = criteriaData.view_config.journey;
    const renderCustomSettings = journey === 'ALLAPP';

    let CurrentSkin = appSkins[criteriaData.config.skin];

    if ([ '557', '558', '569' ].includes(id)) {
        CurrentSkin = AppSkin8;

        if (isMobileDoctorSkin) {
            CurrentSkin = MobileSkin1;
        }
    }

    if (shouldRunSkin9) {
        CurrentSkin = AppSkin9;
    }

    if (isMobile) {
        CurrentSkin = MobileSkin1;
    }

    const allProps: AppSkinProps = {
        id,
        results,
        criteriaData,
        criteriaConstraints,
        setCriteriaConstraints,
        onRerank,
        onNotifyApply,
        onRerankMany,
        onPageChange,
        criteriaLoading,
        onSliderChange,
        onSliderBulkChange,
        onTradeoffValueChange,
        onCriteriaApply,
        onResetTradeOffs,
        fieldInitialValues: initialCriteriaData.current.fields,
        pageInfo,
        resultLoading,
        selectedProductIds,
        setSelectedProductIds,
        renderCustomSettings,
    };

    const components = {
        Tradeoffs: (
            <AppCriteriaFields
                {...allProps}
            />
        ),
        Settings: (
            <AppCriteriaSettings
                {...allProps}
                criteriaFields={criteriaData.fields}
                renderCustomSettings={renderCustomSettings}
                handleLastStepClick={() => {
                    setCurrentStep(
                        currentStep => currentStep + 1,
                    );
                }}
                renderNewTitle
            />
        ),
        Recommend: (
            <AppProductResults
                {...allProps}
            />
        ),
        Infer: (
            <AppProductResults
                {...allProps}
            />
        ),
        ReRank: (
            <AppProductResults
                {...allProps}
            />
        ),
        OReRank: (
            <AppProductResults
                {...allProps}
            />
        ),
    };

    const allSteps = {
        TCRIR: [
            {
                title: 'Recommend',
                component: components.Recommend,
            },
            {
                title: 'Settings',
                component: components.Tradeoffs,
            },
            {
                title: 'Infer',
                component: components.Infer,
            },
            {
                title: 'Tradeoffs',
                component: components.Tradeoffs,
            },
            {
                title: 'Recommend',
                component: components.Recommend,
            },
            {
                title: 'All',
                component: <CurrentSkin {...allProps}/>,
            },
        ],
        CTRIR: [
            {
                title: 'Settings',
                component: components.Settings,
            },
            {
                title: 'Tradeoffs',
                component: components.Tradeoffs,
            },
            {
                title: 'Recommend',
                component: components.Recommend,
            },
            {
                title: 'Infer',
                component: components.Infer,
            },
            {
                title: 'Recommend',
                component: components.Recommend,
            },
            {
                title: 'All',
                component: <CurrentSkin {...allProps}/>,
            },
        ],
        RIRAAR: [
            {
                title: 'Re-rank',
                component: components.ReRank,
            },
            {
                title: 'Infer',
                component: components.Infer,
            },
            {
                title: 'Recommend',
                component: components.Recommend,
            },
            {
                title: 'Tradeoffs',
                component: components.Tradeoffs,
            },
            {
                title: 'Settings',
                component: components.Settings,
            },
            {
                title: 'Recommend',
                component: components.Recommend,
            },
            {
                title: 'All',
                component: <CurrentSkin {...allProps}/>,
            },
        ],
        RCIROIR: [
            {
                title: 'Re-rank',
                component: components.ReRank,
            },
            {
                title: 'Settings',
                component: components.Settings,
            },
            {
                title: 'Infer',
                component: components.Infer,
            },
            {
                title: 'Recommend',
                component: components.Recommend,
            },
            {
                title: 'Option to Re-Rank',
                component: components.OReRank,
            },
            {
                title: 'Infer',
                component: components.Infer,
            },
            {
                title: 'Recommend',
                component: components.Recommend,
            },
            {
                title: 'All',
                component: <CurrentSkin {...allProps}/>,
            },
        ],
        All: [
            {
                title: 'All',
                component: <CurrentSkin {...allProps}/>,
            },
        ],
        AI: [
            {
                title: 'All',
                component: <CurrentSkin {...allProps}/>,
            },
        ],
        ALLAPP: [
            {
                title: 'Settings',
                component: components.Settings,
            },
            {
                title: 'All',
                component: <CurrentSkin {...allProps}/>,
            },
        ],
        CTALLAPP: [
            {
                title: 'Settings',
                component: components.Settings,
            },
            {
                title: 'Tradeoffs',
                component: components.Tradeoffs,
            },
            {
                title: 'All',
                component: <CurrentSkin {...allProps}/>,
            },
        ],
        TCALLAPP: [
            {
                title: 'Tradeoffs',
                component: components.Tradeoffs,
            },
            {
                title: 'Settings',
                component: components.Settings,
            },
            {
                title: 'All',
                component: <CurrentSkin {...allProps}/>,
            },
        ],
        Custom: [],
    };

    const appSteps = allSteps[journey] || allSteps.All;

    return (
        <div className="application">
            <OraChatBot/>
            <HorizontalLinearStepper
                steps={appSteps}
                activeStep={currentStep}
                setActiveStep={setCurrentStep}
                showProgressbar={![ 'All', 'ALLAPP' ].includes(journey) && appConfigs.show.progressbar}
                showActionButtons={journey !== 'All'}
                renderCustomSettings={renderCustomSettings}
            />
        </div>
    );
};

export default ApplicationV2;